import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

interface WeeklyTasksManagerProps {
  weeklyPlan: {
    id: string;
    main_priority: string;
    additional_tasks: string[];
    completed_tasks?: string[];
    main_priority_completed?: boolean;
  };
  onUpdate: () => void;
}

function WeeklyTasksManager({ weeklyPlan, onUpdate }: WeeklyTasksManagerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [mainPriority, setMainPriority] = useState(weeklyPlan.main_priority);
  const [mainPriorityCompleted, setMainPriorityCompleted] = useState(weeklyPlan.main_priority_completed || false);
  const [additionalTasks, setAdditionalTasks] = useState([...weeklyPlan.additional_tasks]);
  const [completedTasks, setCompletedTasks] = useState(weeklyPlan.completed_tasks || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddTask = () => {
    setAdditionalTasks([...additionalTasks, '']);
  };

  const handleTaskChange = (index: number, value: string) => {
    const newTasks = [...additionalTasks];
    newTasks[index] = value;
    setAdditionalTasks(newTasks);
  };

  const handleRemoveTask = (index: number) => {
    const newTasks = additionalTasks.filter((_, i) => i !== index);
    setAdditionalTasks(newTasks);
    const taskToRemove = additionalTasks[index];
    if (completedTasks.includes(taskToRemove)) {
      setCompletedTasks(completedTasks.filter(task => task !== taskToRemove));
    }
  };

  const handleMainPriorityCompletion = async () => {
    setError(null);
    setLoading(true);

    try {
      const newCompletionStatus = !mainPriorityCompleted;

      const { error: updateError } = await supabase
        .from('weekly_plans')
        .update({ main_priority_completed: newCompletionStatus })
        .eq('id', weeklyPlan.id);

      if (updateError) throw updateError;

      setMainPriorityCompleted(newCompletionStatus);
      onUpdate();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskCompletion = async (task: string) => {
    setError(null);
    setLoading(true);

    try {
      const newCompletedTasks = completedTasks.includes(task)
        ? completedTasks.filter(t => t !== task)
        : [...completedTasks, task];

      const { error: updateError } = await supabase
        .from('weekly_plans')
        .update({ completed_tasks: newCompletedTasks })
        .eq('id', weeklyPlan.id);

      if (updateError) throw updateError;

      setCompletedTasks(newCompletedTasks);
      onUpdate();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const filteredTasks = additionalTasks.filter(task => task.trim() !== '');
      const newCompletedTasks = completedTasks.filter(task => 
        filteredTasks.includes(task)
      );

      const { error: updateError } = await supabase
        .from('weekly_plans')
        .update({
          main_priority: mainPriority,
          additional_tasks: filteredTasks,
          completed_tasks: newCompletedTasks,
          main_priority_completed: mainPriorityCompleted
        })
        .eq('id', weeklyPlan.id);

      if (updateError) throw updateError;

      setIsEditing(false);
      setCompletedTasks(newCompletedTasks);
      onUpdate();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setMainPriority(weeklyPlan.main_priority);
    setMainPriorityCompleted(weeklyPlan.main_priority_completed || false);
    setAdditionalTasks([...weeklyPlan.additional_tasks]);
    setCompletedTasks(weeklyPlan.completed_tasks || []);
  };

  if (!isEditing) {
    return (
      <div>
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Main Priority</h3>
          <div className="mt-2">
            <button
              onClick={handleMainPriorityCompletion}
              disabled={loading}
              className={`flex items-center gap-2 text-left w-full p-2 rounded-md transition-colors ${
                mainPriorityCompleted
                  ? 'bg-green-50 text-green-700'
                  : 'hover:bg-gray-50'
              }`}
            >
              <CheckCircleIcon
                className={`h-5 w-5 ${
                  mainPriorityCompleted
                    ? 'text-green-500'
                    : 'text-gray-300'
                }`}
              />
              <span className="text-gray-900">
                {weeklyPlan.main_priority}
              </span>
            </button>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Additional Tasks</h3>
          <ul className="space-y-2">
            {weeklyPlan.additional_tasks.map((task, index) => (
              <li key={index} className="flex items-center gap-2">
                <button
                  onClick={() => handleTaskCompletion(task)}
                  disabled={loading}
                  className={`flex items-center gap-2 text-left w-full p-2 rounded-md transition-colors ${
                    completedTasks.includes(task)
                      ? 'bg-green-50 text-green-700'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <CheckCircleIcon
                    className={`h-5 w-5 ${
                      completedTasks.includes(task)
                        ? 'text-green-500'
                        : 'text-gray-300'
                    }`}
                  />
                  <span className="text-gray-900">
                    {task}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button
            onClick={() => setIsEditing(true)}
            className="mt-4 text-sm text-gray-900 hover:text-gray-600 transition-colors"
          >
            Edit Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-500">
          Main Priority
          <div className="flex items-center gap-2 mt-1">
            <textarea
              value={mainPriority}
              onChange={(e) => setMainPriority(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
              rows={3}
              required
              disabled={loading}
            />
          </div>
          <div className="mt-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={mainPriorityCompleted}
                onChange={(e) => setMainPriorityCompleted(e.target.checked)}
                className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                disabled={loading}
              />
              <span className="ml-2 text-sm text-gray-500">Mark as completed</span>
            </label>
          </div>
        </label>
      </div>

      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-2">Additional Tasks</h3>
        {additionalTasks.map((task, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={task}
              onChange={(e) => handleTaskChange(index, e.target.value)}
              className="block flex-1 rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
              placeholder={`Task ${index + 1}`}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => handleRemoveTask(index)}
              className="text-red-600 hover:text-red-700"
              disabled={loading}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={handleAddTask}
          className="text-sm text-gray-900 hover:text-gray-600 transition-colors"
          disabled={loading}
        >
          + Add Task
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={loading}
          className={`px-4 py-2 text-sm rounded-md ${
            loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gray-900 hover:bg-gray-800'
          } text-white`}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={loading}
          className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default WeeklyTasksManager;